/**
 * Shared number formatting for Streaming Economics views.
 *
 * Revenue figures are split into sign + amount so the amount can be set in
 * Bebas Neue (--font-display) while the currency sign stays muted, matching
 * the Figma treatment on the ranked cards.
 */

export interface SplitCurrency {
  sign: string;
  amount: string;
}

/** 1_748_424_010 -> { sign: '$', amount: '1.75B' } */
export function splitUsd(value: number): SplitCurrency {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return { sign: '$', amount: `${(value / 1_000_000_000).toFixed(2)}B` };
  if (abs >= 1_000_000) return { sign: '$', amount: `${(value / 1_000_000).toFixed(1)}M` };
  if (abs >= 1_000) return { sign: '$', amount: `${(value / 1_000).toFixed(0)}K` };
  return { sign: '$', amount: value.toFixed(0) };
}

/** 1_748_424_010 -> '$1.75B' */
export function formatUsd(value: number): string {
  const { sign, amount } = splitUsd(value);
  return `${sign}${amount}`;
}

const usdCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Chart labels and axes: 800_000_000 -> '$800M', 8_712_000 -> '$8.7M' (d360's compact style). */
export function formatUsdCompact(value: number): string {
  return usdCompact.format(value);
}

/** 93_100_000 -> '93.1M' */
export function formatCount(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(0);
}

/** 17.34085 -> '$17.34' */
export function formatArpu(value: number): string {
  return `$${value.toFixed(2)}`;
}

/** Already-a-percentage value: 2.2951 -> '2.3%' */
export function formatPct(value: number, digits = 1): string {
  return `${value.toFixed(digits)}%`;
}

/** Fractional share: 0.014786 -> '1.48%' */
export function formatShare(fraction: number, digits = 2): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Signed delta for QoQ movement: -0.07 -> '−0.07%' (true minus sign) */
export function formatDelta(value: number, digits = 1): string {
  const rounded = Number(value.toFixed(digits));
  if (rounded === 0) return `0.${'0'.repeat(digits)}%`;
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(digits)}%`;
}

export type Direction = 'up' | 'down' | 'flat';

export function directionOf(value: number, epsilon = 0.05): Direction {
  if (value > epsilon) return 'up';
  if (value < -epsilon) return 'down';
  return 'flat';
}

/**
 * Normalises a quarter to 'QN YYYY' for prose and headers.
 * Accepts both shapes the API uses:
 *   - '2026 Q2'    (platforms leaderboard rows)
 *   - '2026-06-30' (quarter-end date, from the filter lists)
 */
export function prettyQuarter(quarter: string): string {
  const qMatch = quarter.match(/^(\d{4})\s*(Q\d)$/);
  if (qMatch) return `${qMatch[2]} ${qMatch[1]}`;

  const dMatch = quarter.match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (dMatch) {
    const year = dMatch[1];
    const q = Math.floor((Number(dMatch[2]) - 1) / 3) + 1;
    return `Q${q} ${year}`;
  }

  return quarter;
}

/** Amazon Prime Video is too long for a card header. */
export function shortPlatform(name: string): string {
  return name.replace('Amazon Prime Video', 'Prime Video');
}

export function greeting(date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return '☀️ Good Morning';
  if (hour < 18) return '🌆 Good Afternoon';
  return '🌙 Good Evening';
}
